/**
 * State a new declaration.
 *
 * Creating needs three things the edit form does not: somewhere to put it, an
 * ID, and - for kinds whose keyword is itself a choice - which sort it is. The
 * package list comes from /api/packages so nobody has to type a qualified
 * name, and the keyword has no default because defaulting would quietly make
 * every new ICD element a message.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { ApiError, PAGE_LIMIT, api } from '../../api'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import type { KindSchema, PackageRow, PartRow } from '../../schema'
import ElementPicker from '../common/ElementPicker'

export default function CreateEntityDialog({
  kind,
  onClose,
  onCreated,
  owner,
}: {
  kind: KindSchema
  onClose: () => void
  onCreated: (identifier: string) => void
  /**
   * Declare it INSIDE this element rather than in a chosen package - a
   * sub-state inside its machine. The server decides what may own what
   * (`OWNING_KEYWORDS`); this only saves typing a qualified name that is not in
   * the package list at all, since a machine is not a package.
   */
  owner?: { qualifiedName: string; label: string }
}) {
  const toasts = useToasts()
  const [packages, setPackages] = useState<PackageRow[]>([])
  const [pkg, setPkg] = useState<string>(owner?.qualifiedName ?? '')
  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState('')
  const [typeName, setTypeName] = useState(kind.default_type)
  const [keyword, setKeyword] = useState(kind.keywords[0]?.name ?? '')
  const [statement, setStatement] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // No package list when the owner is fixed: it is not a package, and the
  // picker it would fill is not rendered.
  // Only the kind's own base package (issue #114); a kind with none - a port -
  // gets every package back.
  const fixedOwner = owner?.qualifiedName ?? ''
  const [packageWarning, setPackageWarning] = useState('')
  useEffect(() => {
    if (fixedOwner) return
    api
      .packagesFor(kind.name)
      .then((answer) => {
        setPackages(answer.items)
        setPackageWarning(answer.warning)
      })
      .catch(() => setPackages([]))
  }, [fixedOwner, kind.name])

  // A PORT belongs inside a part definition (issue #52) - that is where it
  // crosses a boundary - so the Ports screen's New offers the part defs beside
  // the packages. The server is still what decides (`OWNING_KEYWORDS`); this
  // only puts the right destinations in the list.
  const ownedByParts = kind.name === 'design_port'
  const [partOwners, setPartOwners] = useState<PartRow[]>([])
  useEffect(() => {
    if (fixedOwner || !ownedByParts) return
    api
      .parts({ limit: PAGE_LIMIT })
      .then((page) => setPartOwners(page.items.filter((row) => row.is_definition)))
      .catch(() => setPartOwners([]))
  }, [fixedOwner, ownedByParts])

  const destinations = [
    ...partOwners.map((row) => ({
      value: String(row.qualified_name),
      label: String(row.qualified_name),
      note: `part def ${row.name || row.id}`,
    })),
    ...packages.map((entry) => ({ value: entry.qualified_name })),
  ]

  const submit = async () => {
    setBusy(true)
    setFailure('')
    setFieldErrors({})
    try {
      const body: Record<string, unknown> = { package: pkg, identifier }
      if (name) body.name = name
      if (typeName) body.type_name = typeName
      if (kind.keywords.length) body.keyword = keyword
      if (statement) body.statement = statement

      const created = await api.create(kind.plural, body)
      toasts.succeeded(`${created.identifier} created`)
      onCreated(created.identifier)
    } catch (error) {
      const failed = error as ApiError
      setFieldErrors(failed.fields ?? {})
      if (!Object.keys(failed.fields ?? {}).length) setFailure(failed.message)
    } finally {
      setBusy(false)
    }
  }

  const ready = Boolean(pkg && identifier) && (!kind.keywords.length || Boolean(keyword))

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        New {kind.label.toLowerCase()}
        {owner ? ` inside ${owner.label}` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}

          {owner ? (
            <TextField
              label="Inside"
              value={owner.qualifiedName}
              size="small"
              error={Boolean(fieldErrors.package)}
              helperText={fieldErrors.package || 'Declared in this element’s body'}
              slotProps={{ input: { readOnly: true, sx: { fontFamily: MONOSPACE } } }}
            />
          ) : (
          <ElementPicker
            options={destinations}
            value={pkg || null}
            onChange={setPkg}
            label={ownedByParts ? 'Part or package' : 'Package'}
            required
            error={Boolean(fieldErrors.package)}
            helperText={
              fieldErrors.package ||
              packageWarning ||
              (ownedByParts
                ? 'The part definition the port is on; a package leaves it on no part'
                : 'Where the declaration goes')
            }
          />
          )}

          <TextField
            label="ID"
            required
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            error={Boolean(fieldErrors.identifier)}
            helperText={fieldErrors.identifier || 'The short name, e.g. SUB_GNC_9'}
            size="small"
            slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
          />

          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || 'The display name; defaults to the ID'}
            size="small"
          />

          {kind.keywords.length > 0 && (
            <FormControl size="small" required>
              <InputLabel id="new-keyword">Sort</InputLabel>
              <Select
                labelId="new-keyword"
                label="Sort"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
              >
                {kind.keywords.map((choice) => (
                  <MenuItem key={choice.name} value={choice.name}>
                    {choice.label} — <code>{choice.name}</code>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Autocomplete
            freeSolo
            options={kind.types.map((choice) => choice.name)}
            value={typeName}
            onChange={(_event, next) => setTypeName(next ?? '')}
            onInputChange={(_event, next) => setTypeName(next)}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Type"
                required={kind.type_required}
                error={Boolean(fieldErrors.type_name)}
                helperText={fieldErrors.type_name || 'Any valid type; the list is a shortcut'}
              />
            )}
          />

          <TextField
            label="Statement"
            value={statement}
            onChange={(event) => setStatement(event.target.value)}
            multiline
            minRows={2}
            error={Boolean(fieldErrors.statement)}
            helperText={fieldErrors.statement}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={!ready || busy}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
